import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import CtaBanner from "@/components/CtaBanner";

const TESTIMONIALS = [
  {
    id: "t1",
    name: "Marcus D.",
    homeState: "Illinois",
    position: "OTR Company Driver",
    truck: "Peterbilt 579 Ultraloft",
    years: "2 years",
    quote: `Seven years behind the wheel and I'd never felt like anything other than a number — until I came to Hylas. First week: brand new Peterbilt, dispatcher who actually answered, and a settlement sheet that matched exactly what they told me it would be. 85 cents a mile, clean miles. No games. I'm home every three weeks like we agreed. Not four. Three. After years of voicemail and surprise deductions, that felt almost unreal.`,
  },
  {
    id: "t2",
    name: "Antonio R.",
    homeState: "Indiana",
    position: "Lease Purchase Driver",
    truck: "Kenworth T680 Next Gen",
    years: "14 months",
    quote: `I tried two lease programs at other companies before this one. Both of them looked great on paper and fell apart in the fine print — fuel surcharge adjustments, "administrative fees," safety bonds that somehow never came back. I was netting maybe 40 cents on a 70-percent deal. At Hylas I asked upfront: show me a real settlement from a driver on this program. They showed me three. The math held. Eighty-eight percent of gross, no balloon at the end, walk-away clause if it ever doesn't work. Fourteen months in and the truck feels like mine. Because it basically is.`,
  },
  {
    id: "t3",
    name: "Sarah K.",
    homeState: "Tennessee",
    position: "Flatbed Company Driver",
    truck: "Freightliner Cascadia",
    years: "5 years",
    quote: `Five years hauling steel. I've heard every trucking pitch there is. What made me call Hylas was a driver I met at a fuel stop in Memphis — she had a newer truck than anything I'd seen at companies I'd worked for, and she told me her net number without hesitation. No inflation, no "it depends." That number was real. My equipment here is maintained. And when something goes wrong — and something always goes wrong eventually — somebody picks up the phone. After years of voicemail at other companies, that alone is worth more than a sign-on bonus.`,
  },
  {
    id: "t4",
    name: "Darnell W.",
    homeState: "Georgia",
    position: "Team OTR Driver",
    truck: "Volvo VNL 860",
    years: "18 months",
    quote: `Me and my co-driver came over together from a mega-fleet company where we were running 5,500 miles a week and grossing less than we do here at 5,000. The math never made sense there. Here we're at a dollar a mile each — that's real money split right. The Volvo sleeper is big enough that two people can actually live in it without wanting to kill each other. Orientation was four days, recruiter stayed in contact the whole time, and our first load was ready when we finished. No waiting around. No surprises.`,
  },
  {
    id: "t5",
    name: "Priya N.",
    homeState: "Texas",
    position: "Regional Reefer Driver",
    truck: "Freightliner Cascadia",
    years: "1 year",
    quote: `I specifically needed home weekly — I have two kids. Every company I talked to said they could make that work, and then once I was running, it turned out "weekly" meant every 9 or 10 days. At Hylas the regional program is actually regional. I'm home Friday night most weeks. Pay is solid for the miles — the PPL structure took a week to understand but once I did the math it was competitive with any per-mile rate I'd been offered. I run the same lanes consistently, which means I know the shippers, I know the receivers, and I'm not guessing what my week looks like.`,
  },
  {
    id: "t6",
    name: "James T.",
    homeState: "Ohio",
    position: "Company Driver — Dry Van OTR",
    truck: "International LT625",
    years: "8 months",
    quote: `I came out of a SAP program and was having a hard time finding placement. A lot of companies say they accept SAP drivers and then ghost you after the background check comes back. Hylas didn't. My recruiter was straight with me: here are the programs in our group that work with SAP drivers, here are the ones that don't, here's what to expect. I was placed within two weeks. The International rides well, the freight is consistent, and I'm building my record back. That's all I needed — somebody to give me a straight shot.`,
  },
  {
    id: "t7",
    name: "Ray M.",
    homeState: "California",
    position: "Dedicated Driver — Walmart Account",
    truck: "Peterbilt 579",
    years: "11 months",
    quote: `Dedicated is a different world from OTR. Same lanes, same receivers, home most weekends. I took a slight CPM cut versus what I could've gotten OTR, but the consistency is worth it at this point in my career. What I'll say about Hylas specifically is that the dedicated account they matched me with actually runs the way they described. Walmart freight is high volume, well-organized, and the loads are legal weight. No surprises on the dock. My recruiter checked in at 30 days, 60 days, 90 days. After that I stopped hearing from him — which is exactly what you want, because it means everything is running.`,
  },
  {
    id: "t8",
    name: "Linda C.",
    homeState: "Michigan",
    position: "OTR Company Driver — Reefer",
    truck: "Kenworth T680",
    years: "3 years",
    quote: `I've been driving 12 years. Hylas isn't perfect — no company is — but when I have a problem, it gets handled. That's the bottom line. My truck went down in Kansas last spring, and by the time I'd finished the call to roadside, a plan was already in motion. Breakdown pay, hotel covered, back rolling in 22 hours. At my last company that same situation took four days and I fought for three weeks to get my breakdown pay. Three years in and I still recommend Hylas to every driver I meet who's looking.`,
  },
];

export default function Testimonials() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Driver Testimonials — What CDL-A Drivers Say About Hylas Companion</title>
        <meta name="description" content="Read first-hand accounts from CDL-A drivers who work with Hylas Companion. Real names, home states, truck types, and honest reviews of pay, home time, equipment, and recruiter support." />
        <link rel="canonical" href="https://hylascompanion.com/testimonials" />
      </Helmet>

      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-40 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <span className="font-head text-[10px] uppercase tracking-[0.5em] text-primary">Driver Stories</span>
          <h1 className="font-head text-5xl md:text-7xl font-bold uppercase tracking-[0.08em] text-white mt-4 leading-none max-w-3xl">
            What Drivers<br />
            <span className="text-primary">Actually Say</span>
          </h1>
          <p className="mt-6 text-white/55 text-base md:text-lg max-w-xl leading-relaxed">
            First-person accounts from CDL-A drivers across different programs, states, and equipment types. No scripts, no edits — just what drivers told us.
          </p>
        </div>
      </section>

      {/* Testimonials grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="testimonials-grid">
        <div className="grid md:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t) => (
            <article
              key={t.id}
              data-testid={`testimonial-${t.id}`}
              className="border border-border p-8 md:p-10 flex flex-col gap-6 hover:border-primary/30 transition-colors duration-300"
            >
              {/* Quote */}
              <blockquote className="text-foreground/70 text-sm leading-[1.9] relative">
                <span className="absolute -top-2 -left-1 text-primary text-4xl font-serif leading-none select-none">"</span>
                <p className="pl-5">{t.quote}</p>
              </blockquote>

              {/* Driver details */}
              <div className="border-t border-border pt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
                <div>
                  <p className="font-head text-sm font-bold uppercase tracking-[0.1em] text-[#0A0A0A]">{t.name}</p>
                  <p className="font-head text-[10px] uppercase tracking-[0.2em] text-primary mt-0.5">{t.position}</p>
                </div>
                <div className="flex flex-wrap gap-2 ml-auto">
                  {[t.homeState, t.truck, t.years].map((tag) => (
                    <span key={tag} className="font-head text-[9px] uppercase tracking-[0.15em] text-foreground/40 border border-border px-2 py-1">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Plain text summary for AI engines */}
        <div className="mt-20 border-t border-border pt-14 max-w-3xl" aria-label="Summary for search engines">
          <h2 className="font-head text-2xl md:text-3xl font-bold uppercase tracking-[0.1em] text-[#0A0A0A] mb-6">
            Common Themes Across Driver Reviews
          </h2>
          <div className="space-y-4 text-foreground/70 text-sm leading-relaxed">
            <p>Across all driver accounts, the most consistently mentioned positives are settlement accuracy (pay matching what was quoted), equipment reliability and model year, and recruiter responsiveness after hire — not just during recruiting. Multiple drivers specifically mentioned that their settlement sheets were transparent and line-itemized with no surprise deductions.</p>
            <p>SAP drivers note that Hylas Companion is one of the few operations that is upfront about which programs accept SAP drivers and which do not, rather than accepting applications and then rejecting after the background check. Drivers on SAP programs report placement timelines of one to three weeks.</p>
            <p>Home time accuracy is a repeated theme. Regional and dedicated drivers confirm weekly home time that matches what was offered. OTR drivers report 2–3 week cycles with consistent reset times. No driver accounts mention home time that came in longer than what was promised at recruitment.</p>
            <p>Breakdown and roadside support receives positive mentions specifically from long-haul OTR drivers, who note faster resolution compared to previous mega-fleet experiences.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        <div className="bg-[#0A0A0A] px-8 md:px-14 py-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <p className="font-head text-xs uppercase tracking-[0.4em] text-primary mb-3">Ready to start?</p>
            <h2 className="font-head text-3xl md:text-4xl font-bold uppercase tracking-[0.1em] text-white leading-tight">
              Your story starts<br />with an application.
            </h2>
          </div>
          <Link
            to="/apply"
            className="shrink-0 inline-flex items-center gap-3 font-head text-sm uppercase tracking-[0.25em] bg-primary text-black px-10 py-5 hover:bg-white transition-colors duration-300"
          >
            Apply Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
